import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { TtsItemContext } from '../contextApi'
import {
  useGetCurrentSpeakerHasEmotion,
  useGetTtsItemSpeakerProps
} from 'containers/SubSidebar/TtsContent/hooks'
import { dispatch } from 'store'
import { ttsThunks } from 'store/features/tts/thunks'

export const useTtsItemSpeakerFlyout = (closeDropdown: () => void) => {
  const { id } = useContext(TtsItemContext)

  const { langCode, speakerId, instructions } = useGetTtsItemSpeakerProps(id)

  const [ttsItemLangCode, setTtsItemLangCode] = useState<string>(langCode)
  const [ttsActiveSpeakerId, setTtsActiveSpeakerId] =
    useState<number>(speakerId)
  const [ttsItemInstruction, setTtsItemInstruction] =
    useState<string>(instructions)

  const hasEmotion = useGetCurrentSpeakerHasEmotion(
    ttsActiveSpeakerId,
    ttsItemLangCode
  )

  useEffect(() => {
    if (ttsItemLangCode) {
      dispatch(ttsThunks.getSpeakersPerLangCode(ttsItemLangCode))
    }
  }, [ttsItemLangCode])

  const isTtsItemLangCodeChanged = useMemo(
    () => ttsItemLangCode !== langCode,
    [ttsItemLangCode, langCode]
  )
  const isTtsItemSpeakerIdChanged = useMemo(
    () => ttsActiveSpeakerId !== speakerId,
    [ttsActiveSpeakerId, speakerId]
  )
  const isTtsItemInstructionChanged = useMemo(
    () => ttsItemInstruction !== instructions,
    [ttsItemInstruction, instructions]
  )

  const isAnyChanges =
    isTtsItemLangCodeChanged ||
    isTtsItemSpeakerIdChanged ||
    isTtsItemInstructionChanged

  const handleLanguageChange = useCallback(
    (langCode: string) => {
      setTtsItemLangCode(prev => {
        if (prev !== langCode) {
          return langCode
        }
        return prev
      })
    },
    [setTtsItemLangCode]
  )

  const handleVoiceChange = useCallback(
    (speakerId: number) => {
      setTtsActiveSpeakerId(prev => {
        if (prev !== speakerId) {
          return speakerId
        }
        return prev
      })
    },
    [setTtsActiveSpeakerId]
  )

  const handleToneChange = useCallback(
    (tone: string) => {
      setTtsItemInstruction(prev => {
        if (prev !== tone) {
          return tone
        }
        return prev
      })
    },
    [setTtsItemInstruction]
  )

  const handleConfirmButton = useCallback(() => {
    if (isTtsItemLangCodeChanged) {
      dispatch(ttsThunks.setTtsItemLangCode(id, ttsItemLangCode))
    }
    if (isTtsItemSpeakerIdChanged) {
      dispatch(ttsThunks.setTtsItemSpeakerId(id, ttsActiveSpeakerId))
    }
    if (isTtsItemInstructionChanged) {
      dispatch(ttsThunks.setTtsItemInstruction(id, ttsItemInstruction))
    }
    closeDropdown()
  }, [
    closeDropdown,
    id,
    ttsItemLangCode,
    ttsActiveSpeakerId,
    ttsItemInstruction,
    isTtsItemLangCodeChanged,
    isTtsItemSpeakerIdChanged,
    isTtsItemInstructionChanged
  ])

  return {
    ttsItemLangCode,
    handleLanguageChange,
    ttsActiveSpeakerId,
    handleVoiceChange,
    ttsItemInstruction,
    handleToneChange,
    hasEmotion,
    isAnyChanges,
    handleConfirmButton
  }
}
